import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Router} from "@angular/router";
import {RxAuthService} from "../rx-auth/rx-auth.service";
import {lastValueFrom, Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';


@Injectable({
  providedIn: 'root'
})
export class TabularDataDownloadService {

  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: RxAuthService,
    public snack: MatSnackBar,
  ) {
  }

  private apiUrl = `${environment.backend_base}downloadables`;

  getSingleRankingEchelonNames(ranking_rcl_id: string): Observable<any> {
    const body = {ranking_rcl_id: ranking_rcl_id};
    return this.http.post<any>(
      `${this.apiUrl}/get_single_ranking_echelon_names/`,
      body,
      this.auth.httpOptions
    );
  }

  getSingleRetailerEchelonNames(retailer_rcl_id: string): Observable<any> {
    const body = {retailer_rcl_id: retailer_rcl_id};
    return this.http.post<any>(
      `${this.apiUrl}/get_single_retailer_echelon_names/`,
      body,
      this.auth.httpOptions
    );
  }

  getMultiRetailerEchelonNames(query: string, filters: any | null = null): Observable<any> {
    const body = {letter: query, filters: filters};
    return this.http.post<any>(
      `${this.apiUrl}/get_multi_retailer_echelon_names/`,
      body,
      this.auth.httpOptions
    );
  }

  getMultiSupplierEchelonNames(query: string, filters: any | null = null): Observable<any> {
    const body = {letter: query, filters: filters};
    return this.http.post<any>(
      `${this.apiUrl}/get_multi_supplier_echelon_names/`,
      body,
      this.auth.httpOptions
    );
  }

  chooseRunType(data: any, contentType: string): Observable<{ echelons: any[], rcl_ids: string }> {
    switch (contentType) {


      case 'single-ranking':
        return this.getSingleRankingEchelonNames(data.rcl_id).pipe(
          catchError(err => {
            console.error('Error in single-ranking:', err);
            throw err;
          })
        );

      case 'single-retailer':
        return this.getSingleRetailerEchelonNames(data.rcl_id).pipe(
          catchError(err => {
            console.error('Error in single-retailer:', err);
            throw err;
          })
        );

      case 'multi-retailer':
        return this.getMultiRetailerEchelonNames(data.query, data.filters).pipe(
          catchError(err => {
            console.error('Error in multi-retailer:', err);
            throw err;
          })
        );
      case 'multi-supplier':
        return this.getMultiSupplierEchelonNames(data.query, data.filters).pipe(
          catchError(err => {
            console.error('Error in multi-retailer:', err);
            throw err;
          })
        );

      default:
        return of({ echelons: [], rcl_ids: '' });
    }
  }

  async updateAvailableEchelons(
    idsVarToUpdate: any[],
    availableEchelonsVarToUpdate: any[],
    data: any,
    contentType: string
  ): Promise<void> {
    const { echelons, rcl_ids } = await lastValueFrom(
      this.chooseRunType(data, contentType)
    );

    // Update the array contents instead of reassigning
    idsVarToUpdate.length = 0;
    idsVarToUpdate.push(rcl_ids);

    availableEchelonsVarToUpdate.length = 0;
    availableEchelonsVarToUpdate.push(...echelons);
  }

  downloadCSV(payload: any, onComplete: () => void) {
    const url = environment.backend_base + 'downloadables/get_csv_report/';

    this.http.post(url, payload, { responseType: 'blob' }).subscribe({
      next: (response: any) => {
        const file = new Blob([response], { type: 'application/zip' });
        const downloadURL = window.URL.createObjectURL(file);

        const a = document.createElement('a');
        a.href = downloadURL;
        a.download = `${payload.fileName}.zip`;
        a.click();
        console.log('downloaded');
        window.URL.revokeObjectURL(downloadURL);

        if (onComplete) onComplete();
        this.snack.open('Download complete', 'Close', { duration: 5000 });
      },
      error: async (error) => {
        let errorMsg = 'Download failed. Please try again later.';

        this.snack.open(errorMsg, 'Close', { duration: 8000 });
        if (onComplete) onComplete();
      }
    });
  }

  downloadXLSX(payload: any, onComplete: () => void) {
    const url = environment.backend_base + 'downloadables/get_xlsx_report/';

    this.http.post(url, payload, { responseType: 'blob' }).subscribe({
      next: (response: any) => {
        const file = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const downloadURL = window.URL.createObjectURL(file);

        const a = document.createElement('a');
        a.href = downloadURL;
        a.download = `${payload.fileName}.xlsx`;
        a.click();
        console.log('downloaded');
        window.URL.revokeObjectURL(downloadURL);

        if (onComplete) onComplete();
        this.snack.open('Download complete', 'Close', { duration: 5000 });
      },
      error: async (error) => {
        let errorMsg = 'Download failed. Please try again later.';

        this.snack.open(errorMsg, 'Close', { duration: 8000 });
        if (onComplete) onComplete();
      }
    });
  }

  choseDownloadType(payload: any, contentType: string, outFileType:string | null, onComplete: () => void) {
    switch (contentType) {
      case 'single-retailer':
      case 'single-supplier':
        this.downloadXLSX(payload, onComplete);
        break;
      case 'single-ranking':
        if (outFileType === 'csv') {
          this.downloadCSV(payload, onComplete);
        } else if (outFileType === 'xlsx') {
          this.downloadXLSX(payload, onComplete);
        }
        break;

      case 'multi-supplier':
      case 'multi-retailer':
        this.downloadCSV(payload, onComplete);
        break;
      default:
        break
    }
  }

  createPayload(ids: any, rawEchelonData: any, contentType: string, profileName:string): any {

    switch (contentType) {
      case 'single-retailer':
      case 'single-supplier':
        return {
          fileName: profileName,
          rcl_id: ids,
          echelon_ids: rawEchelonData && rawEchelonData.length > 0 ? rawEchelonData.map((d: any) => d.id) : null,
        };

      case 'single-ranking':
        return {
          fileName: profileName,
          ranking_id: ids,
          echelon_ids: rawEchelonData && rawEchelonData.length > 0 ? rawEchelonData.map((d: any) => d.id) : null,
        };

      case 'multi-supplier':
      case 'multi-retailer':
        return {
          fileName:profileName,
          rcl_ids: ids[0],
          echelon_ids: rawEchelonData && rawEchelonData.length > 0 ? rawEchelonData.map((d: any) => d.id) : null,
        };

      default:
        return {};
    }
  }
}
